import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MobileNav } from '../components/MobileNav';
import { Footer } from '../components/Footer';
import SEO, { pageSEO } from '../components/SEO';
import { 
  Sparkles, Clock, Gift, Star, Phone, Mail, 
  ArrowRight, MessageCircle, Heart
} from 'lucide-react';

// Subtle Gold Hearts Component
const SubtleGoldHearts = () => (
  <div className="subtle-gold-hearts">
    {[...Array(16)].map((_, i) => (
      <div key={i} className="subtle-heart">
        <Heart className="w-full h-full fill-current" />
      </div>
    ))}
  </div>
);

const services = [
  {
    id: 'free',
    name: 'Free Consultation',
    price: 'FREE',
    duration: '15 min',
    description: 'Discover how numerology can transform your relationships. Let\'s chat about your goals and see if we\'re a good fit.',
    icon: Gift,
    color: 'from-green-400 to-emerald-500',
    popular: false
  },
  {
    id: '30min',
    name: '30 Minute Reading',
    price: '$75',
    duration: '30 min',
    description: 'Perfect for a focused numerology or compatibility reading. Get insights into your life path, expression number, or relationship dynamics.',
    icon: Clock,
    color: 'from-purple-400 to-purple-600',
    popular: false
  },
  {
    id: '60min',
    name: '60 Minute Reading',
    price: '$125',
    duration: '60 min',
    description: 'A comprehensive session covering your full numerology profile, Chinese zodiac, and detailed compatibility analysis with a partner.',
    icon: Star,
    color: 'from-amber-400 to-amber-600',
    popular: true
  },
  {
    id: '90min',
    name: '90 Minute Reading',
    price: '$150',
    duration: '90 min',
    description: 'The ultimate deep-dive experience. Full numerology profile, compatibility reading, psychic insights, and personalized guidance for your journey.',
    icon: Sparkles,
    color: 'from-pink-400 to-pink-600',
    popular: false
  }
];

export const ServicesPage = () => {
  const venmoUsername = 'Karine-Burt';
  const venmoLink = `https://venmo.com/${venmoUsername}`;

  return (
    <div className="min-h-screen swirl-bg relative">
      <SEO {...pageSEO.services} />
      <SubtleGoldHearts />
      <MobileNav />

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <p className="font-whimsy text-2xl text-[#D4AF37] mb-4 font-bold">✨ Book Your Reading ✨</p>
          <h1 className="font-mystical text-4xl md:text-6xl text-[#2D1B3D] font-bold mb-6">
            Services & Pricing
          </h1>
          <p className="text-[#4A3A5A] text-xl max-w-2xl mx-auto">
            Discover your cosmic path with a personalized numerology reading from Karine Burt
          </p>
        </motion.div>

        {/* About Karine Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="ethereal-card rounded-3xl p-8 md:p-10 mb-12"
        >
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <div className="w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden border-4 border-[#D4AF37] glow-gold shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_soul-match-numbers/artifacts/lem5lq0f_IMG_4333.jpeg" 
                  alt="Karine Burt"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: '50% 25%' }}
                />
              </div>
            </div>
            <div className="text-center md:text-left flex-1">
              <h2 className="font-mystical text-3xl text-[#2D1B3D] font-bold mb-2">Karine Burt</h2>
              <p className="font-whimsy text-xl text-[#D4AF37] font-bold mb-4">Psychic Medium, Numerology Matchmaker & Spiritual Coach</p>
              <p className="text-[#4A3A5A] text-lg leading-relaxed mb-6">
                I'm so much more than a matchmaker — I'm your Spiritual Coach and guide on your journey to wholeness. 
                As a gifted Psychic Medium, I help souls find clarity, healing, and new beginnings. 
                Are you carrying the weight of past relationships? I can help you uncover hidden truths, find closure, and finally 
                put those old connections to rest — freeing your heart to embrace the love you truly deserve.
              </p>
              <p className="text-[#4A3A5A] text-lg leading-relaxed mb-6">
                Through the ancient wisdom of Pythagorean numerology and the Chinese zodiac, combined with my psychic abilities, 
                I'll guide you toward understanding yourself on a deeper level. Whether you're seeking to heal from the past, 
                find your soulmate, or simply gain clarity on your life's path — my readings offer the guidance and enlightenment 
                you need to move forward with confidence and peace.
              </p>
              <p className="text-[#4A3A5A] text-base mb-6">
                Also founder of <a href="https://www.savvysingles.com" target="_blank" rel="noopener noreferrer" className="text-[#8B5CF6] font-bold hover:text-[#D4AF37] underline">SavvySingles.com</a> — A+ Rated with the BBB ⭐
              </p>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4">
                <a 
                  href="tel:678-665-4712" 
                  className="inline-flex items-center gap-2 text-[#5B21B6] hover:text-[#D4AF37] font-semibold transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  678-665-4712
                </a>
                <a 
                  href="mailto:karine@intuitivematchmaker.com" 
                  className="inline-flex items-center gap-2 text-[#5B21B6] hover:text-[#D4AF37] font-semibold transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  karine@intuitivematchmaker.com
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {services.map((service, i) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.1 }}
              className={`ethereal-card rounded-3xl p-8 relative ${service.popular ? 'ring-2 ring-[#D4AF37]' : ''}`}
            >
              {service.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#D4AF37] to-[#B8860B] text-white px-4 py-1 rounded-full text-sm font-bold">
                  Most Popular
                </div>
              )}
              
              <div className="flex items-start justify-between mb-6">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center shadow-lg`}>
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-right">
                  <p className="font-mystical text-3xl text-[#2D1B3D] font-bold">{service.price}</p>
                  <p className="text-[#6D5A7D] text-sm">{service.duration}</p>
                </div>
              </div>
              
              <h3 className="font-mystical text-2xl text-[#2D1B3D] font-bold mb-3">{service.name}</h3>
              <p className="text-[#4A3A5A] leading-relaxed mb-6">{service.description}</p>
              
              {service.price === 'FREE' ? (
                <a href="tel:678-665-4712">
                  <Button className="w-full rounded-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-6">
                    <Phone className="w-5 h-5 mr-2" />
                    Call for Free Consultation
                  </Button>
                </a>
              ) : (
                <a href={venmoLink} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full rounded-full btn-magic font-bold py-6">
                    Book Now
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
              )}
            </motion.div>
          ))}
        </div>

        {/* Venmo Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="ethereal-card rounded-3xl p-8 md:p-12 text-center"
        >
          <h2 className="font-mystical text-3xl text-[#2D1B3D] font-bold mb-6">Payment via Venmo</h2>
          <p className="text-[#4A3A5A] text-lg mb-8 max-w-xl mx-auto">
            After booking, please send payment via Venmo. Include your name and the service you're booking in the note.
          </p>
          
          <div className="flex flex-col items-center gap-6">
            {/* Venmo QR Code */}
            <div className="bg-white rounded-2xl p-4 shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_soul-match-numbers/artifacts/boafu9zh_IMG_0734.jpeg" 
                alt="Venmo QR Code - @Karine-Burt"
                className="w-48 h-48 md:w-64 md:h-64 object-contain"
              />
            </div>
            
            <div>
              <p className="text-[#2D1B3D] text-xl font-bold mb-2">@Karine-Burt</p>
              <a 
                href={venmoLink} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#008CFF] text-white px-8 py-4 rounded-full font-bold hover:shadow-lg hover:scale-105 transition-all text-lg"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.5 3c.9 1.5 1.3 3 1.3 5 0 5.5-4.7 12.7-8.5 17H4.7L2 3.5l6.5-.5.9 11c1.4-2.2 3.1-5.7 3.1-8.1 0-1.9-.3-3.2-.8-4.3L19.5 3z"/>
                </svg>
                Pay with Venmo
              </a>
            </div>
          </div>
        </motion.div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-center mt-12"
        >
          <div className="ethereal-card rounded-3xl p-8 md:p-10">
            <h3 className="font-mystical text-2xl md:text-3xl text-[#2D1B3D] font-bold mb-4">
              Ready to Book Your Reading?
            </h3>
            <p className="text-[#4A3A5A] text-lg mb-6">Questions or ready to schedule? Reach out anytime!</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" data-testid="services-contact-cta">
                <Button className="rounded-full btn-magic px-10 py-6 text-xl font-bold shadow-xl hover:shadow-2xl">
                  <MessageCircle className="w-6 h-6 mr-3" />
                  Contact Me to Book
                </Button>
              </Link>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <a 
                href="tel:678-665-4712" 
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white px-6 py-3 rounded-full font-bold hover:shadow-lg hover:scale-105 transition-all"
                data-testid="services-phone-cta"
              >
                <Phone className="w-5 h-5" />
                678-665-4712
              </a>
              <a 
                href="mailto:karine@intuitivematchmaker.com" 
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#B8860B] text-white px-6 py-3 rounded-full font-bold hover:shadow-lg hover:scale-105 transition-all"
              >
                <Mail className="w-5 h-5" />
                Email Karine
              </a>
            </div>
          </div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default ServicesPage;
